// Per-line product editor on an estimate / quote.
//
// 2026-10-14 (Stephanie, commonsku ideas A–F, Jake approved): ONE page, no tabs.
// Everything about the item is on one scrolling screen, with the item summary
// (margin, subtotal, tax, total) at the bottom, as commonsku has it:
//
//   • Description + product images
//   • Pricing   — one commonsku-style table (ItemPricingTable): colour/size
//                 rows, then the item's added costs as rows, with + Add
//                 breakdown / + Add cost under it. Same onPatchLine /
//                 onPatchVariant writes as the quote page; no money math here.
//   • Decoration — Supplier / 3rd-party decorator switch, one row per imprint
//                 location (name, art tile, proof) with its charges as pricing
//                 rows; the art tile opens the Artwork window.
//   • Item summary — LineItemSummary, which reads the shared money module.
//
// The modal never computes money and never forks the pricing UI — it reuses it.

import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ProductEditModalShell } from "@/components/product-edit/ProductEditModalShell";
import { ItemPricingTable } from "@/components/documents/ItemPricingTable";
import { LineItemSummary } from "@/components/documents/LineItemSummary";
import { SizeBreakdownEditor, type ColourOption } from "@/components/orders/SizeBreakdownEditor";
import { docLineImageUrl, type DocLine } from "@/components/documents/types";
import {
  OrderLineImageEditor,
  OrderLineDecorationEditor,
  OrderItemCostRows,
  type EditorDecoration,
} from "@/pages/OrderLineEditor";
import { SupplierSaleNote, type SaleLine } from "@/components/catalog/SupplierSaleNote";

export function OrderLineEditModal(props: {
  orderId: string;
  /** The owning project — threaded to the Artwork window's file picker so it
   *  can list this project's files. */
  projectId: string;
  line: DocLine;
  currency: string;
  /** The quote's tax rate, for the item summary. */
  taxRatePct?: string | null;
  /** The colours/sizes this product comes in, for "+ Add breakdown". */
  colours?: ColourOption[];
  /** SanMar colours/sizes on sale now — shown beside the cost, never used as it. */
  saleLines?: SaleLine[];
  /** "+ Add cost" / Display-Subtract are on (Jake's DDL has run). */
  itemCostsReady?: boolean;
  detailQueryKey: readonly unknown[];
  onPatchLine: (body: Record<string, unknown>) => void;
  onPatchVariant: (variantId: string, body: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const { orderId, projectId, line, currency, taxRatePct, colours, saleLines, itemCostsReady, detailQueryKey, onPatchLine, onPatchVariant, onClose } =
    props;
  // commonsku's lock on Retail — starts UNLOCKED (Stephanie 2026-10-14): a cost
  // change keeps the row's margin and re-prices. Locked keeps the price.
  // Locked by default everywhere (Stephanie 2026-10-27): changing a cost never
  // moves the price unless you unlock it.
  const [retailLocked, setRetailLocked] = useState(true);
  // Open straight away while the item has no colors yet (Stephanie 2026-10-26).
  const [breakdownOpen, setBreakdownOpen] = useState(line.variants.length === 0);
  const [addingCost, setAddingCost] = useState<"fixed" | "per_piece" | null>(null);
  // The Artwork window sits on top of this one; while it is open, Escape and
  // the backdrop belong to it, not to the item window.
  const [artworkOpen, setArtworkOpen] = useState(false);

  const snapshot = line.productSnapshot ?? {};
  const name = snapshot.name?.trim() || line.description?.trim() || "Product";
  const imageUrl = docLineImageUrl(line) ?? undefined;
  // Imprint locations only — not the "+ Add cost" holder.
  const decorationCount = (line.decorations ?? []).filter((d) => d.kind !== "item_costs").length;

  // The two editor bodies bind to their own leaner shape (id / quantityTotal /
  // snapshot imageUrls / decorations). Build it explicitly so no document-only
  // fields leak in and the decorations array is always present.
  const editorLine = {
    id: line.id,
    quantityTotal: line.quantityTotal,
    productSnapshot: line.productSnapshot,
    decorations: (line.decorations ?? []) as EditorDecoration[],
  };

  // No tab strip — the shell hides it for an empty list.
  const tabs: never[] = [];

  return (
    <ProductEditModalShell
      ariaLabel={`Edit ${name}`}
      imageUrl={imageUrl}
      title={name}
      subtitle={
        <>
          {snapshot.sku ? `SKU ${snapshot.sku} · ` : ""}
          {snapshot.supplierName ? `${snapshot.supplierName} · ` : ""}
          {line.quantityTotal.toLocaleString()} {line.quantityTotal === 1 ? "unit" : "units"}
        </>
      }
      tabs={tabs}
      wide
      onClose={() => {
        if (!artworkOpen) onClose();
      }}
    >
      <div className="space-y-8 px-2">
        <ItemSection title="Description">
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_auto]">
            <DescriptionField
              orderId={orderId}
              lineId={line.id}
              description={line.description ?? ""}
              detailQueryKey={detailQueryKey}
            />
            <OrderLineImageEditor
              orderId={orderId}
              line={editorLine}
              detailQueryKey={detailQueryKey}
            />
          </div>
        </ItemSection>

        <ItemSection title="Pricing">
          {/* Like commonsku (Stephanie 2026-10-14): one table — the colour /
              size rows, then this item's added costs as more rows — and the
              links under it. Decoration charges live under their location;
              the totals are in the summary at the bottom. */}
          <ItemPricingTable
            line={line}
            currency={currency}
            retailLocked={retailLocked}
            onToggleLock={() => setRetailLocked((v) => !v)}
            onPatchLine={onPatchLine}
            onPatchVariant={onPatchVariant}
          >
            {itemCostsReady === true && (
              <OrderItemCostRows
                orderId={orderId}
                line={editorLine}
                detailQueryKey={detailQueryKey}
                adding={addingCost}
                onDoneAdding={() => setAddingCost(null)}
              />
            )}
          </ItemPricingTable>
          <SupplierSaleNote lines={saleLines} currency={currency} className="mt-3" />
          {breakdownOpen && line.serviceType === "product" && (
            <div className="mt-3">
              <SizeBreakdownEditor
                orderId={orderId}
                lineItemId={line.id}
                variants={line.variants.map((v) => ({ ...v, unitCost: v.unitCost ?? null }))}
                colours={colours ?? []}
                detailQueryKey={detailQueryKey}
                onClose={() => setBreakdownOpen(false)}
              />
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-[15px] font-semibold text-rust-500">
            {line.serviceType === "product" && !breakdownOpen && (
              // commonsku's "Add Breakdown": one row per colour/size, each with
              // its own qty, cost, margin and retail (a 2XL upcharge).
              <button type="button" onClick={() => setBreakdownOpen(true)} className="hover:text-rust-600">
                {line.variants.length > 0 ? "Edit breakdown" : "+ Add breakdown"}
              </button>
            )}
            {itemCostsReady === true ? (
              <>
                <button type="button" onClick={() => setAddingCost("fixed")} className="hover:text-rust-600">
                  + Add cost
                </button>
                <button type="button" onClick={() => setAddingCost("per_piece")} className="hover:text-rust-600">
                  + Add per-piece cost
                </button>
              </>
            ) : (
              <span className="text-sm font-normal text-brown-300">
                "+ Add cost" switches on once Jake runs one database command (it's in the work log).
              </span>
            )}
          </div>
        </ItemSection>

        <ItemSection title="Decoration" count={decorationCount}>
          <OrderLineDecorationEditor
            orderId={orderId}
            projectId={projectId}
            line={editorLine}
            detailQueryKey={detailQueryKey}
            onArtworkWindow={setArtworkOpen}
            itemCostsReady={itemCostsReady === true}
          />
        </ItemSection>

        {/* Summary at the bottom, like commonsku: margin left, totals right. */}
        <LineItemSummary line={line} currency={currency} taxRatePct={taxRatePct} />
      </div>
    </ProductEditModalShell>
  );
}

/** One labelled band of the one-page item window (same look as the
 *  presentation editor's sections). */
function ItemSection(props: { title: string; count?: number; children: React.ReactNode }) {
  const { title, count, children } = props;
  return (
    <section>
      <div className="mb-3 flex items-baseline gap-2 border-b border-brown-100 pb-2">
        <h3 className="font-display text-base font-semibold uppercase tracking-wide text-brown-400">
          {title}
        </h3>
        {count !== undefined && count > 0 && (
          <span className="rounded-full bg-cream-100 px-2 py-0.5 text-xs font-semibold text-brown-400">
            {count}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

// ─── Description ────────────────────────────────────────────────────────
// Reuses the existing line PATCH write path (/orders/:id/lines/:lineId with a
// `description` field). SKU / supplier sit in the window header (read-only,
// from the immutable product snapshot).

function DescriptionField(props: {
  orderId: string;
  lineId: string;
  description: string;
  detailQueryKey: readonly unknown[];
}) {
  const { orderId, lineId, description, detailQueryKey } = props;
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(description);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (next: string) =>
      api(`/orders/${orderId}/lines/${lineId}`, {
        method: "PATCH",
        body: JSON.stringify({ description: next }),
      }),
    onError: (err) =>
      setError(err instanceof Error ? err.message : "Couldn't save."),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  const dirty = draft.trim() !== description.trim();

  // Empty → the vendor's description, filled in and saved on its own
  // (Stephanie 2026-10-26: "the description from the vendor has
  // disappeared"; "things should save automatically"). Only ever into an
  // empty box — never over what someone wrote.
  const filled = useRef(false);
  useEffect(() => {
    if (filled.current || description.trim() !== "") return;
    filled.current = true;
    void api<{ description: string | null }>(`/orders/${orderId}/lines/${lineId}/vendor-description`)
      .then((r) => {
        const vendor = r.description?.trim();
        if (!vendor) return;
        setDraft((d) => (d.trim() === "" ? vendor : d));
        save.mutate(vendor);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, lineId, description]);

  return (
    <div className="space-y-2">
      <label className="block">
        <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-300">
          What the client reads
        </span>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          // Saves on its own when you click away (Stephanie 2026-10-14: "not
          // click edit and save").
          onBlur={() => {
            if (dirty) save.mutate(draft.trim());
          }}
          rows={7}
          maxLength={8000}
          placeholder="What should show for this product on the document?"
          className="w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-[15px] leading-relaxed text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200 disabled:bg-cream-50"
        />
      </label>
      {error && <p className="text-sm text-rust-600">{error}</p>}
      <p className="text-sm text-brown-300" aria-live="polite">
        {save.isPending ? "Saving…" : save.isSuccess && !dirty ? "Saved." : "Saves when you click away."}
      </p>
    </div>
  );
}
